/**
 * First-move hint memory (card Rl0vzkZ2hdAe).
 *
 * The four `.tab-hint` one-liners (thread / us / life / notebook) name the one
 * action that starts each tab. They were rendered unconditionally, so the hint
 * came back on every visit long after the couple had done what it describes —
 * Jonathan's report of 2026-10-08 was that they never dismiss.
 *
 * A hint is remembered as SEEN per couple (not per device session): the flag
 * lives in localStorage next to the onboarding-tour flag, keyed by coupleId so
 * a second pair signing in on the same browser starts fresh. Once seen the
 * hint is not rendered again — no dismiss button, no timer, no re-show.
 *
 * State is read through `useHintSeen`, a `useSyncExternalStore` subscription,
 * so marking a hint seen inside a panel (which is where the action happens)
 * re-renders the screen that draws the hint, and a second tab on the same
 * device follows via the `storage` event.
 */
import { useSyncExternalStore } from 'react';
import { session } from '../shell/bridge';

/** The three `.tab-hint` one-liners still drawn as paragraphs (thread / life /
 *  notebook). The Questions screen dropped its paragraph for an ⓘ tooltip
 *  beside the heading (card tFMiqgWdBdqW), so `us` is no longer a hint key. */
export type HintKey = 'thread' | 'life' | 'notebook';

const STORE_KEY = 'pear.hints.seen';

/** coupleId -> hints that couple has already acted on. */
type SeenMap = Record<string, HintKey[]>;

let cache: SeenMap | null = null;
const listeners = new Set<() => void>();

/** Read (or re-read) the whole map. Never throws: storage can be unavailable,
 *  and a hint that cannot remember itself must not break the screen. */
function load(): SeenMap {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    cache = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as SeenMap) : {};
  } catch {
    cache = {};
  }
  return cache;
}

function coupleKey(): string {
  try {
    return session()?.coupleId ?? '__none__';
  } catch {
    return '__none__';
  }
}

function write(map: SeenMap): void {
  cache = map;
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable — the in-memory copy still holds for this load */
  }
}

function emit(): void {
  for (const fn of listeners) fn();
}

/** Has this couple already done the thing this hint names? */
export function isHintSeen(key: HintKey): boolean {
  return (load()[coupleKey()] ?? []).includes(key);
}

/** Record that the hint's action has happened. Idempotent, silent on failure. */
export function markHintSeen(key: HintKey): void {
  const map = load();
  const ck = coupleKey();
  const list = map[ck] ?? [];
  if (list.includes(key)) return;
  map[ck] = [...list, key];
  write(map);
  emit();
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== null && e.key !== STORE_KEY) return;
    cache = null;
    onStoreChange();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStorage);
  };
}

/** `true` while the hint should still be drawn. */
export function useHintSeen(key: HintKey): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isHintSeen(key),
    () => false,
  );
}
